import { Exclude, plainToInstance, Type } from 'class-transformer'
import truncate from 'lodash/truncate'
import { Entity } from '@core/entities/entity'
import { PermitMultipleChoiceAnswer } from '../../permit/PermitMultipleChoiceAnswer'
import { ITemplateQuestion } from './ITemplateQuestion'
import {
  AllowedMediaTypes,
  RequiredOptionalType,
  SubmissionDocument,
  SubmissionProof,
  TemplateQuestionRegisterItemPropertyType,
} from '@core/entities/template/TemplateQuestion/types'
import {
  LinkedChecklistResult,
  LinkedPermitResult,
} from '@core/entities/linkedProcesses'

import { PermitStatus } from '@core/entities/permit/PermitBase'
import { QuestionType } from '@core/entities/template/QuestionType'
import { ChecklistStatus } from '@core/entities/checklist/Checklist'
import { localizedStrings } from '@core/strings'
import { InspectionItemAnswer } from '@core/entities/InspectionItemAnswer'

export class TemplateQuestion extends Entity<ITemplateQuestion> {
  id: string

  questionType: QuestionType

  body: string

  required?: boolean

  allowedMediaTypes?: AllowedMediaTypes

  requirePhotoFromCamera?: boolean

  requiresSignature?: RequiredOptionalType

  requiresProof?: RequiredOptionalType

  requiresAdditionalInformation?: RequiredOptionalType

  requireProofFromCamera?: boolean

  minAnswers?: number

  maxAnswers?: number

  @Type(() => PermitMultipleChoiceAnswer)
  answers?: Array<PermitMultipleChoiceAnswer>

  validAnswer?: string

  invalidAnswer?: string

  invalidDescription?: string

  validAnswerIds?: Array<string>

  @Exclude()
  acknowledgeInformationDocument?: File

  acknowledgeInformationDocumentKey?: string

  acknowledgeInformationDocumentDownloadUrl?: string

  dataTableId?: number

  permitTemplateLinkingIds?: Array<number>
  permitTemplateLinkingStatuses?: Array<PermitStatus>

  checklistTemplateLinkingIds?: Array<number>
  checklistTemplateLinkingStatuses?: Array<ChecklistStatus>
  checklistTemplateLinks?: Array<{ id: number; name: string }>

  inspectionTemplateLinkingIds?: Array<number>
  inspectionTemplateLinkingStatuses?: Array<ChecklistStatus>
  inspectionTemplateLinks?: Array<{ id: number; name: string }>

  durationMinutesOptions?: Array<number>

  linkedRegisterTemplateId?: number

  previewPropertyIds?: Array<string>

  pinnedFilterIds?: Array<string>

  allowMultipleLinkedRegisterItems?: boolean

  // We cannot use IRegisterTemplate interface here, as it causes a circular reference error in typescript
  linkedRegisterTemplate?: {
    id: number
  }

  /*
   * CIRCULAR DEPENDENCY PREVENTION:
   * Cannot use RegisterItem class here due to circular import chain:
   * TemplateQuestion → RegisterItem → TemplateQuestionRegisterItemPropertyType → TemplateQuestion
   * RegisterItem.getPreviewPropertiesData() method requires TemplateQuestionRegisterItemPropertyType
   * which is defined in this module, creating a circular dependency when we import RegisterItem class.
   *
   * The circular reference is coming because of this:
   * TemplateQuestion.ts ──────► RegisterItem.ts
   *       ▲                        │
   *       │                        │
   *       └────────────────────────┘
   */
  registerItems?: Array<any>

  previewProperties?: Array<TemplateQuestionRegisterItemPropertyType>

  pinnedFilters?: Array<TemplateQuestionRegisterItemPropertyType>

  //from this point only the answered questions have these types
  invalid?: boolean
  submissionText?: string
  signatureKey?: string
  signatureUrl?: string
  submissionProofs?: Array<SubmissionProof>
  submissionDocuments?: Array<SubmissionDocument>
  acknowledged?: boolean
  isHidden?: boolean

  linkedPermitsIds?: Array<number>
  @Type(() => LinkedPermitResult)
  linkedPermitsResults?: Array<LinkedPermitResult>

  linkedChecklistsIds?: Array<number>
  @Type(() => LinkedChecklistResult)
  linkedChecklistsResults?: Array<LinkedChecklistResult>

  linkedInspectionsIds?: Array<number>
  @Type(() => LinkedChecklistResult)
  linkedInspectionsResults?: Array<LinkedChecklistResult>

  //from this point only the answered questions have these types
  localSignature?: string
  localSubmissionProofs?: Array<File>
  localSubmissionDocuments?: Array<File>

  @Type(() => InspectionItemAnswer)
  inspectionItemAnswers?: Array<InspectionItemAnswer>

  preSelected?: boolean

  public static new(payload: unknown): TemplateQuestion {
    const entity = plainToInstance(TemplateQuestion, payload)

    return entity
  }

  public static getQuestionTypesList(): Array<QuestionType> {
    return [
      'ALPHANUMERIC',
      'DATE',
      'DATE_ONLY',
      'MULTIPLE_CHOICE',
      'NUMERIC',
      'PRE_CONDITION',
      'DOCUMENT',
      'INFO',
      'ACKNOWLEDGE_INFORMATION',
      'LINK_PERMITS',
      'LINK_CHECKLISTS',
      'LINK_INSPECTIONS',
      'LINK_REGISTER_ITEMS',
      'INSPECTION_ITEM',
      'CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN',
    ]
  }

  public static getQuestionTypeText(questionType: QuestionType): string {
    const format = (questionType: QuestionType) => {
      return questionType
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/(?:^|\s)\S/g, (a) => a.toUpperCase())
    }
    switch (questionType) {
      case 'DATE':
        return localizedStrings.dateAndTime
      case 'DATE_ONLY':
        return localizedStrings.date
      case 'CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN':
        return localizedStrings.countdown
      case 'DOCUMENT':
        return localizedStrings.media
      case 'QUIZ_QUESTION':
        return localizedStrings.quizQuestion
      default:
        return format(questionType)
    }
  }

  public static getQuestionIndexName({
    groupIndex,
    questionIndex,
    questionGroup,
  }): string {
    return `Question${questionGroup ? ' Group ' : ' '}${groupIndex + 1}${
      questionGroup ? '' : `-${questionIndex + 1 || 1}`
    }`
  }

  public static isCorrectQuizAnswer(
    question: ITemplateQuestion,
    selectedAnswerIds: Set<string>,
  ): boolean {
    const valid = question.validAnswerIds ?? []
    if (selectedAnswerIds.size !== valid.length) return false
    return valid.every((id) => selectedAnswerIds.has(id))
  }

  public static withAllAnswersDeselected(
    question: ITemplateQuestion,
  ): ITemplateQuestion {
    return {
      ...question,
      answers: (question.answers ?? []).map((a) => ({
        ...a,
        selected: false,
      })),
    }
  }

  public static toggleAnswerSelection(
    question: ITemplateQuestion,
    answerId: string,
  ): ITemplateQuestion {
    return {
      ...question,
      answers: (question.answers ?? []).map((a) => {
        if (a.id !== answerId) return a
        return { ...a, selected: !a.selected }
      }),
    }
  }

  public static getBodyFormattedText(
    body: string,
    shouldTruncate: boolean = true,
  ): string {
    const maxLength = 30
    const bodyWithoutHtmlTags = body
      ?.replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/&nbsp;/g, '')

    return shouldTruncate
      ? truncate(bodyWithoutHtmlTags, { length: maxLength })
      : bodyWithoutHtmlTags
  }

  /**
   * Format a question's index name and body text for display.
   */
  public static getQuestionDisplayText({
    groupIndex,
    questionIndex,
    questionType,
    body,
    truncate = false,
  }: {
    groupIndex: number
    questionIndex: number
    questionType: string
    body: string
    truncate?: boolean
  }): string {
    return `${TemplateQuestion.getQuestionIndexName({
      groupIndex,
      questionIndex,
      questionGroup: questionType === 'QUESTION_GROUP',
    })} ${TemplateQuestion.getBodyFormattedText(body, truncate)}`
  }

  /**
   * Format a question group's display text (e.g. "Question Group 3 - Permit Type").
   */
  public static getQuestionGroupDisplayText(
    groupIndex: number,
    title?: string,
  ): string {
    const indexName = localizedStrings.questionGroupIndex(groupIndex + 1)
    return title ? `${indexName} - ${title}` : indexName
  }

  public getBodyFormattedText(): string {
    return TemplateQuestion.getBodyFormattedText(this.body)
  }

  public getAllowedMediaTypesText(): string {
    switch (this.allowedMediaTypes) {
      case 'DOCUMENTS_ONLY':
        return localizedStrings.documentsOnly
      case 'PHOTOS_ONLY':
        return localizedStrings.photosOnly
      case 'DOCUMENTS_AND_PHOTOS':
        return localizedStrings.documentsAndPhotos
      default:
        return ''
    }
  }
}
